import { useEffect, useState } from "react";

/**
 * Subscribes to a CSS media query and returns whether it currently matches.
 */
export const useMediaQuery = (query: string): boolean => {
  const getMatches = () => {
    if (typeof window === "undefined") {
      return false;
    }

    return window.matchMedia(query).matches;
  };

  const [matches, setMatches] = useState(getMatches);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const mediaQuery = window.matchMedia(query);

    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    setMatches(mediaQuery.matches);

    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, [query]);

  return matches;
};

const breakpoints = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
} as const;

/**
 * Convenience hooks for the default Tailwind CSS breakpoints
 */
export const useIsSmallOrAbove = () =>
  useMediaQuery(`(min-width: ${breakpoints.sm}px)`);
export const useIsMediumOrAbove = () =>
  useMediaQuery(`(min-width: ${breakpoints.md}px)`);
export const useIsLargeOrAbove = () =>
  useMediaQuery(`(min-width: ${breakpoints.lg}px)`);
export const useIsExtraLargeOrAbove = () =>
  useMediaQuery(`(min-width: ${breakpoints.xl}px)`);

export const useIsSmallOrBelow = () =>
  useMediaQuery(`(max-width: ${breakpoints.sm - 1}px)`);
export const useIsMediumOrBelow = () =>
  useMediaQuery(`(max-width: ${breakpoints.md - 1}px)`);
export const useIsLargeOrBelow = () =>
  useMediaQuery(`(max-width: ${breakpoints.lg - 1}px)`);
export const useIsExtraLargeOrBelow = () =>
  useMediaQuery(`(max-width: ${breakpoints.xl - 1}px)`);

/**
 * True when the primary pointing device cannot hover and is coarse (typical phones/tablets).
 */
export const useIsTouchDevice = () =>
  useMediaQuery("(hover: none) and (pointer: coarse)");
